// 커스텀 사이드바 — refine ThemedSiderV2를 대체한다.
// 접힌(아이콘만 보이는) 상태에서 하위 메뉴가 있는 항목에 화살표를 그리는 대신,
// 마우스를 올리면 오른쪽에 하위 메뉴 목록이 플라이아웃으로 펼쳐지게 한다.
// (펼쳐진 상태의 아코디언 동작·활성 항목 강조·모바일 드로어 등 나머지는 ThemedSiderV2와 동일)

import React, { CSSProperties, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Typography from "@mui/material/Typography";

import ListOutlined from "@mui/icons-material/ListOutlined";
import Logout from "@mui/icons-material/Logout";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import Dashboard from "@mui/icons-material/Dashboard";

import {
  CanAccess,
  ITreeMenu,
  useIsExistAuthentication,
  useLogout,
  useTitle,
  useTranslate,
  useRouterContext,
  useRouterType,
  useLink,
  useMenu,
  useRefineContext,
  useActiveAuthProvider,
  pickNotDeprecated,
  useWarnAboutChange,
} from "@refinedev/core";
import {
  ThemedTitleV2 as DefaultTitle,
  useThemedLayoutContext,
} from "@refinedev/mui";
import type { RefineThemedLayoutV2SiderProps } from "@refinedev/mui";

// 하위 메뉴를 펼치고 닫을 때의 유예 시간(ms).
// 아이콘 → 플라이아웃 사이로 마우스를 옮기는 동안 깜빡이며 닫히는 것을 방지한다.
const FLYOUT_CLOSE_DELAY = 150;

export const Sider: React.FC<RefineThemedLayoutV2SiderProps> = ({
  Title: TitleFromProps,
  render,
  meta,
  activeItemDisabled = false,
}) => {
  const {
    siderCollapsed,
    setSiderCollapsed,
    mobileSiderOpen,
    setMobileSiderOpen,
  } = useThemedLayoutContext();

  const drawerWidth = () => {
    if (siderCollapsed) return 56;
    return 240;
  };

  const t = useTranslate();
  const routerType = useRouterType();
  const Link = useLink();
  const { Link: LegacyLink } = useRouterContext();
  const ActiveLink = routerType === "legacy" ? LegacyLink : Link;
  const { hasDashboard } = useRefineContext();
  const translate = useTranslate();

  const { menuItems, selectedKey, defaultOpenKeys } = useMenu({ meta });
  const isExistAuthentication = useIsExistAuthentication();
  const TitleFromContext = useTitle();
  const authProvider = useActiveAuthProvider();
  const { warnWhen, setWarnWhen } = useWarnAboutChange();
  const { mutate: mutateLogout } = useLogout({
    v3LegacyAuthProviderCompatible: Boolean(authProvider?.isLegacy),
  });

  const [open, setOpen] = useState<{ [k: string]: any }>({});

  // 접힌 상태에서 호버로 펼쳐진 플라이아웃 — key + 앵커 엘리먼트
  const [flyoutKey, setFlyoutKey] = useState<string | null>(null);
  const flyoutAnchorRef = useRef<Record<string, HTMLElement | null>>({});
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openFlyout = (key: string) => {
    clearCloseTimer();
    setFlyoutKey(key);
  };

  const scheduleCloseFlyout = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setFlyoutKey(null);
    }, FLYOUT_CLOSE_DELAY);
  };

  React.useEffect(() => {
    setOpen((previous) => {
      const previousKeys: string[] = Object.keys(previous);
      const previousOpenKeys = previousKeys.filter((key) => previous[key]);

      const uniqueKeys = new Set([...previousOpenKeys, ...defaultOpenKeys]);
      const uniqueKeysRecord = Object.fromEntries(
        Array.from(uniqueKeys.values()).map((key) => [key, true])
      );
      return uniqueKeysRecord;
    });
  }, [defaultOpenKeys]);

  // 사이드바가 펼쳐지면(접힘 해제) 잔여 플라이아웃을 닫는다.
  React.useEffect(() => {
    if (!siderCollapsed) setFlyoutKey(null);
  }, [siderCollapsed]);

  const RenderToTitle = TitleFromProps ?? TitleFromContext ?? DefaultTitle;

  const handleClick = (key: string) => {
    setOpen({ ...open, [key]: !open[key] });
  };

  // insideFlyout: 플라이아웃(Popper) 안에서 재사용될 때 true.
  // 플라이아웃 자체가 이미 라벨을 다 보여주므로 리프 항목의 중복 Tooltip을 끈다.
  const renderTreeView = (
    tree: ITreeMenu[],
    selectedKey?: string,
    insideFlyout = false
  ) => {
    return tree.map((item: ITreeMenu) => {
      const { icon, label, route, name, children, parentName, meta, options } = item;
      const isOpen = open[item.key || ""] || false;

      const isSelected = item.key === selectedKey;
      const isNested = !(
        pickNotDeprecated(meta?.parent, options?.parent, parentName) === undefined
      );

      if (children.length > 0) {
        // 접힌 상태에서는 화살표 대신 호버 플라이아웃으로 하위 메뉴를 보여준다.
        const isFlyoutTarget = siderCollapsed;
        const isFlyoutOpen = isFlyoutTarget && flyoutKey === item.key;

        const buttonNode = (
          <ListItemButton
            ref={(el: HTMLElement | null) => {
              flyoutAnchorRef.current[item.key || ""] = el;
            }}
            onMouseEnter={() => {
              if (isFlyoutTarget) openFlyout(item.key || "");
            }}
            onMouseLeave={() => {
              if (isFlyoutTarget) scheduleCloseFlyout();
            }}
            onClick={() => {
              if (siderCollapsed) {
                // 클릭하면 기존처럼 사이드바를 펼치고 그룹도 함께 연다(호버 플라이아웃의 보조 동작).
                setSiderCollapsed(false);
                setFlyoutKey(null);
                if (!isOpen) handleClick(item.key || "");
              } else {
                handleClick(item.key || "");
              }
            }}
            sx={{
              pl: isNested ? 4 : 2,
              justifyContent: "center",
              marginTop: "8px",
            }}
          >
            <ListItemIcon
              sx={{
                justifyContent: "center",
                minWidth: "28px",
                transition: "margin-right 0.3s",
                marginRight: siderCollapsed ? "0px" : "12px",
                color: "currentColor",
                "& .MuiSvgIcon-root": { fontSize: 26 },
              }}
            >
              {icon ?? <ListOutlined />}
            </ListItemIcon>
            <ListItemText
              primary={label}
              primaryTypographyProps={{ noWrap: true, fontSize: "15px" }}
            />
            {/* 접힌 상태에서는 화살표를 그리지 않는다 — 하위 메뉴 존재 여부는 호버 플라이아웃으로 드러난다. */}
            {!siderCollapsed &&
              (isOpen ? (
                <ExpandLess sx={{ color: "text.icon" }} />
              ) : (
                <ExpandMore sx={{ color: "text.icon" }} />
              ))}
          </ListItemButton>
        );

        return (
          <CanAccess
            key={item.key}
            resource={name.toLowerCase()}
            action="list"
            params={{ resource: item }}
          >
            <div
              key={item.key}
              onMouseLeave={() => {
                if (isFlyoutTarget) scheduleCloseFlyout();
              }}
            >
              {siderCollapsed ? (
                buttonNode
              ) : (
                <Tooltip title={label ?? name} placement="right" disableHoverListener arrow>
                  {buttonNode}
                </Tooltip>
              )}
              {!siderCollapsed && (
                <Collapse in={open[item.key || ""]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {renderTreeView(children, selectedKey)}
                  </List>
                </Collapse>
              )}
              {isFlyoutTarget && (
                <Popper
                  open={isFlyoutOpen}
                  anchorEl={flyoutAnchorRef.current[item.key || ""]}
                  placement="right-start"
                  sx={{ zIndex: 1200 }}
                  modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
                >
                  <ClickAwayListener onClickAway={() => setFlyoutKey(null)}>
                    <Paper
                      elevation={4}
                      onMouseEnter={() => openFlyout(item.key || "")}
                      onMouseLeave={scheduleCloseFlyout}
                      sx={{ minWidth: 200, py: 0.5, borderRadius: 1.5 }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ display: "block", px: 2, py: 0.75, color: "text.secondary", fontWeight: 600 }}
                      >
                        {label ?? name}
                      </Typography>
                      <List disablePadding>{renderTreeView(children, selectedKey, true)}</List>
                    </Paper>
                  </ClickAwayListener>
                </Popper>
              )}
            </div>
          </CanAccess>
        );
      }

      const linkStyle: CSSProperties =
        activeItemDisabled && isSelected ? { pointerEvents: "none" } : {};

      return (
        <CanAccess
          key={item.key}
          resource={name.toLowerCase()}
          action="list"
          params={{ resource: item }}
        >
          <Tooltip
            title={label ?? name}
            placement="right"
            disableHoverListener={!siderCollapsed || insideFlyout}
            arrow
          >
            <ListItemButton
              component={ActiveLink}
              to={route}
              selected={isSelected}
              style={linkStyle}
              onClick={() => {
                setMobileSiderOpen(false);
                if (insideFlyout) setFlyoutKey(null);
              }}
              sx={{
                pl: isNested ? 4 : 2,
                py: isNested ? 1.25 : 1,
                justifyContent: "center",
                color: isSelected ? "primary.main" : "text.primary",
              }}
            >
              <ListItemIcon
                sx={{
                  justifyContent: "center",
                  transition: "margin-right 0.3s",
                  marginRight: siderCollapsed ? "0px" : "12px",
                  minWidth: "28px",
                  color: "currentColor",
                  "& .MuiSvgIcon-root": { fontSize: 26 },
                }}
              >
                {icon ?? <ListOutlined />}
              </ListItemIcon>
              <ListItemText primary={label} primaryTypographyProps={{ noWrap: true, fontSize: "15px" }} />
            </ListItemButton>
          </Tooltip>
        </CanAccess>
      );
    });
  };

  const dashboard = hasDashboard ? (
    <CanAccess resource="dashboard" action="list">
      <Tooltip
        title={translate("dashboard.title", "Dashboard")}
        placement="right"
        disableHoverListener={!siderCollapsed}
        arrow
      >
        <ListItemButton
          component={ActiveLink}
          to="/"
          selected={selectedKey === "/"}
          onClick={() => {
            setMobileSiderOpen(false);
          }}
          sx={{
            pl: 2,
            py: 1,
            justifyContent: "center",
            color: selectedKey === "/" ? "primary.main" : "text.primary",
          }}
        >
          <ListItemIcon
            sx={{
              justifyContent: "center",
              minWidth: "28px",
              transition: "margin-right 0.3s",
              marginRight: siderCollapsed ? "0px" : "12px",
              color: "currentColor",
              "& .MuiSvgIcon-root": { fontSize: 26 },
            }}
          >
            <Dashboard />
          </ListItemIcon>
          <ListItemText
            primary={translate("dashboard.title", "Dashboard")}
            primaryTypographyProps={{ noWrap: true, fontSize: "15px" }}
          />
        </ListItemButton>
      </Tooltip>
    </CanAccess>
  ) : null;

  const handleLogout = () => {
    if (warnWhen) {
      const confirm = window.confirm(
        t("warnWhenUnsavedChanges", "Are you sure you want to leave? You have unsaved changes.")
      );

      if (confirm) {
        setWarnWhen(false);
        mutateLogout();
      }
    } else {
      mutateLogout();
    }
  };

  const logout = isExistAuthentication && (
    <Tooltip title={t("buttons.logout", "Logout")} placement="right" disableHoverListener={!siderCollapsed} arrow>
      <ListItemButton key="logout" onClick={() => handleLogout()} sx={{ justifyContent: "center" }}>
        <ListItemIcon
          sx={{
            justifyContent: "center",
            minWidth: "28px",
            transition: "margin-right 0.3s",
            marginRight: siderCollapsed ? "0px" : "12px",
            color: "currentColor",
            "& .MuiSvgIcon-root": { fontSize: 26 },
          }}
        >
          <Logout />
        </ListItemIcon>
        <ListItemText primary={t("buttons.logout", "Logout")} primaryTypographyProps={{ noWrap: true, fontSize: "15px" }} />
      </ListItemButton>
    </Tooltip>
  );

  const items = renderTreeView(menuItems, selectedKey);

  const renderSider = () => {
    if (render) {
      return render({ dashboard, logout, items, collapsed: siderCollapsed });
    }
    return (
      <>
        {dashboard}
        {items}
        {logout}
      </>
    );
  };

  const drawer = (
    <List disablePadding sx={{ flexGrow: 1, paddingTop: "16px" }}>
      {renderSider()}
    </List>
  );

  return (
    <>
      <Box
        sx={{
          width: { xs: drawerWidth() },
          display: { xs: "none", md: "block" },
          transition: "width 0.3s ease",
        }}
      />
      <Box
        component="nav"
        sx={{
          position: "fixed",
          zIndex: 1101,
          width: { sm: drawerWidth() },
          display: "flex",
        }}
      >
        <Drawer
          variant="temporary"
          elevation={2}
          open={mobileSiderOpen}
          onClose={() => setMobileSiderOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { sm: "block", md: "none" } }}
        >
          <Box sx={{ width: drawerWidth() }}>
            <Box sx={{ height: 64, display: "flex", alignItems: "center", paddingLeft: "16px", fontSize: "14px" }}>
              <RenderToTitle collapsed={false} />
            </Box>
            {drawer}
          </Box>
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              width: drawerWidth(),
              overflow: "hidden",
              transition: "width 200ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
            },
          }}
          open
        >
          <Paper
            elevation={0}
            sx={{
              fontSize: "14px",
              width: "100%",
              height: 64,
              display: "flex",
              flexShrink: 0,
              alignItems: "center",
              justifyContent: siderCollapsed ? "center" : "space-between",
              // 로고 아이콘(32px)이 각 상태에서 실제로 중앙에 오도록 paddingLeft를
              // 값 자체로 계산해서 고정한다(정렬 기준을 바꾸는 게 아니라 항상
              // flex-start). 접힘(56px 폭)일 땐 (56-32)/2=12px, 펼침일 땐 기존
              // 디자인값 16px. 12↔16 사이 4px 정도의 미세한 이동은 남지만,
              // 접힌 상태에서 좌우 여백이 비대칭(16 vs 8)으로 보이던 것보다
              // 아래 메뉴 아이콘들과의 중앙 정렬이 맞는 게 우선이라 이렇게 둔다.
              paddingLeft: siderCollapsed ? "12px" : "16px",
              paddingRight: siderCollapsed ? 0 : "8px",
              variant: "outlined",
              borderRadius: 0,
              borderBottom: (theme) => `1px solid ${theme.palette.action.focus}`,
            }}
          >
            <RenderToTitle collapsed={siderCollapsed} />
            {!siderCollapsed && (
              <IconButton size="small" onClick={() => setSiderCollapsed(true)}>
                {<ChevronLeft />}
              </IconButton>
            )}
          </Paper>
          <Box sx={{ flexGrow: 1, overflowX: "hidden", overflowY: "auto" }}>{drawer}</Box>
        </Drawer>
      </Box>
    </>
  );
};
